import { useMemo } from "react";
import { useLoaderData, useLocation, useNavigate, useSearchParams, useSubmit } from "react-router-dom";

import UserInfo from "src/components/common/userInfo";
import ServerFooter from "src/components/common/ServerFooter";
import {
    getDatefromWeekNum0,
    getDateEndFromWeekNum,
    pmEmails,
    standbyWeeks,
    weekDays,
} from "src/components/common/functions";
import Export from "./export";

type CheckInDay = {
    unlock?: boolean;
    morning?: boolean;
    afternoon?: boolean;
    doOT?: string;
    working?: string;
    notWorkingReason?: string;
    commuteOrHotel?: string;
    division?: string;
    workingOn?: string;
    acknowledged?: boolean;
};

type CheckInPerson = {
    email: string;
    userName: string;
    firstName?: string;
    lastName?: string;
    hireDate: string;
    quitDate?: string;
    outside?: string;
    residence?: string;
    phoneNumber?: string;
    lanId?: string;
    [week: string]: any;
};

type LoaderData = {
    forms: CheckInPerson[];
    week: string;
    day: number;
};

export default function AllCheckIn() {
    const { forms, week, day } = useLoaderData() as LoaderData;
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();
    const submit = useSubmit();
    const category = searchParams.get("category") ?? "";
    const filterWords = searchParams.get("q") ?? "";
    const projectManagerEmails = pmEmails as string[];

    const checkDate = useMemo(() => getDatefromWeekNum0(week, day), [week, day]);
    const filterWordsArray = useMemo(
        () => filterWords.toLowerCase().split(" ").filter(value => value.length > 0),
        [filterWords]
    );

    const filteredForms = useMemo(() => forms.filter((form) => {
        if (projectManagerEmails.includes(form.email)) return false;
        if (!checkDate) return false;
        if (category && form.outside !== category) return false;
        const hireDate = new Date(form.hireDate.replace(/-/g, "/"));
        if (checkDate <= hireDate) return false;
        if (form.quitDate) {
            const quitDate = new Date(form.quitDate.replace(/-/g, "/"));
            if (checkDate > quitDate) return false;
        }
        if (filterWordsArray.length > 0) {
            const formString = JSON.stringify(Object.values(form)).toLowerCase();
            if (!filterWordsArray.every(value => formString.includes(value))) return false;
        }
        return true;
    }), [forms, checkDate, category, filterWordsArray, projectManagerEmails]);

    const page = Number(searchParams.get("page") ?? 1);
    const limit = Number(searchParams.get("limit") ?? 50);
    const currentForms = useMemo(
        () => filteredForms.slice((page - 1) * limit, page * limit),
        [filteredForms, page, limit]
    );

    const handleUnlock = (form: CheckInPerson) => {
        const formData = new FormData();
        const currentDay = form[week]?.form?.[day] as CheckInDay | undefined;
        formData.append("userEmail", form.email);
        formData.append("wk", week);
        formData.append("wkDay", String(day));
        formData.append("unlock", !currentDay?.unlock ? "yes" : "no");
        formData.append("redirectTo", location.pathname + location.search);
        submit(formData, {
            method: "post",
            action: `/AllCheckIn/Unlock${location.search}`,
        });
    };

    return (
        <div className = "cssContent pb8rem">
            <nav className = "my-3 d-flex align-items-end flex-wrap gap-3">
                <div>
                    <label className = "small text-muted fw-bold mb-1" htmlFor = "allCheckInSearch">Search</label>
                    <input
                        id = "allCheckInSearch"
                        className = "form-control w-auto"
                        type = "text"
                        value = {filterWords}
                        onChange = {e => updateSearch(setSearchParams, searchParams, "q", e.currentTarget.value, true)}
                    />
                </div>
                <div>
                    <label className = "small text-muted fw-bold mb-1" htmlFor = "allCheckInWeek">Week</label>
                    <select
                        id = "allCheckInWeek"
                        className = "form-control w-auto"
                        value = {week}
                        onChange = {e => navigate(`/AllCheckIn/${e.currentTarget.value}/${day}${location.search}`)}
                    >
                        {standbyWeeks.map((value, index) => (
                            <option key = {index} value = {value}>{getDateEndFromWeekNum(value)}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className = "small text-muted fw-bold mb-1" htmlFor = "allCheckInDay">Day</label>
                    <select
                        id = "allCheckInDay"
                        className = "form-control w-auto"
                        value = {day}
                        onChange = {e => navigate(`/AllCheckIn/${week}/${e.currentTarget.value}${location.search}`)}
                    >
                        {weekDays.map((value, index) => <option key = {index} value = {index}>{value}</option>)}
                    </select>
                </div>
                <div>
                    <label className = "small text-muted fw-bold mb-1" htmlFor = "allCheckInCategory">Category</label>
                    <select
                        id = "allCheckInCategory"
                        className = "form-control w-auto"
                        value = {category}
                        onChange = {e => updateSearch(setSearchParams, searchParams, "category", e.currentTarget.value, true)}
                    >
                        <option value = "">All</option>
                        <option value = "locator">Mark and Locator</option>
                        <option value = "qew">QEW</option>
                        <option value = "standby">Standby</option>
                    </select>
                </div>
                {filteredForms.length > 0 ? <Export filteredForms = {filteredForms} week = {week} weekDay = {day} /> : null}
            </nav>

            {!filteredForms.length ? (
                <div className = "cssLoading">Empty</div>
            ) : (
                <>
                    <div className = "table-responsive">
                        <table className = "table text-center tableAllBorder">
                            <thead>
                                <tr className = "stickyHeader">
                                    <th>Name</th>
                                    <th>Unlock</th>
                                    <th>Morning</th>
                                    <th>Afternoon</th>
                                    <th>OT</th>
                                    <th>Acknowledged</th>
                                    <th>Working</th>
                                    <th>Not Working Reason</th>
                                    <th>Commute or Hotel</th>
                                    <th>Division</th>
                                    <th>Working On</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentForms.map((form) => {
                                    const formData = form[week]?.form?.[day] as CheckInDay | undefined;
                                    return (
                                        <tr id = {form.email} key = {form.email} className = "pointer">
                                            <th>
                                                <UserInfo name = {form.userName} id = {form.email} />
                                            </th>
                                            {formData ? (
                                                <>
                                                    <th>
                                                        <button
                                                            type = "button"
                                                            className = {`m-1 btn btn-${formData.unlock ? "primary" : "info"}`}
                                                            onClick = {() => handleUnlock(form)}
                                                        >
                                                            Set {formData.unlock ? "Lock" : "Unlock"}
                                                        </button>
                                                    </th>
                                                    <th style = {{ color: formData.morning ? "green" : "red" }}>{formData.morning ? "Yes" : ""}</th>
                                                    <th style = {{ color: formData.afternoon ? "green" : "red" }}>{formData.afternoon ? "Yes" : ""}</th>
                                                    <th style = {{ color: formData.doOT ? "green" : "red" }}>{formData.doOT}</th>
                                                    <th style = {{ color: formData.acknowledged ? "green" : "red" }}>{formData.acknowledged ? "Yes" : ""}</th>
                                                    <th style = {{ color: "green" }}>{formData.working}</th>
                                                    <th style = {{ color: "green" }}>{formData.notWorkingReason}</th>
                                                    <th style = {{ color: formData.working ? "green" : "red" }}>{formData.commuteOrHotel}</th>
                                                    <th style = {{ color: formData.working ? "green" : "red" }}>{formData.division}</th>
                                                    <th style = {{ color: formData.working ? "green" : "red" }}>{formData.workingOn}</th>
                                                </>
                                            ) : null}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <ServerFooter length = {filteredForms.length} />
                </>
            )}
        </div>
    );
}

function updateSearch(
    setSearchParams: ReturnType<typeof useSearchParams>[1],
    searchParams: URLSearchParams,
    key: string,
    value: string,
    resetPage = false
) {
    const next = new URLSearchParams(searchParams);
    next.set(key, value);
    if (resetPage) next.set("page", "1");
    setSearchParams(next);
}
